import { Component, OnDestroy, OnInit } from "@angular/core";
import { PlatformQueryService } from "@core/services/platform-query.service";
import { SocketClusterService } from "@core/services/socket-cluster.service";
import { HomeSideBarItem } from "@shared/models/platform-tool-models";
import { IPlatformHomeMagicBar } from "@shared/components/platform-tool/platform-tool-magic-bar/models/magic-bar-models";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ScenarioActivityDto, ScenarioActivityRow } from "./scenario-activity.models";
import { ScenarioActivityQueryFactory } from "./scenario-activity-query.factory";

@Component({
  standalone: false,
  selector: "app-scenario-activity",
  templateUrl: "./scenario-activity.component.html",
  styleUrls: ["./scenario-activity.component.scss"],
})
export class ScenarioActivityComponent
  implements OnInit, OnDestroy, IPlatformHomeMagicBar
{
  data: any;
  homeSideBarItem: HomeSideBarItem;

  activities: ScenarioActivityRow[] = [];

  private unsubscribe$ = new Subject<void>();
  private channelName: string;
  private durationInterval: any;

  constructor(
    private platformQueryService: PlatformQueryService,
    private socketClusterService: SocketClusterService
  ) {}

  ngOnInit(): void {
    const scenarioId = this.data?.ScenarioId;
    if (!scenarioId) {
      return;
    }

    this.loadInitialData(scenarioId);
    this.subscribeToChannel(scenarioId);
    this.startDurationTimer();
  }

  private loadInitialData(scenarioId: string): void {
    const query = ScenarioActivityQueryFactory.getScenarioActivityQuery(scenarioId);
    this.platformQueryService
      .executeQuery(query)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        const items: ScenarioActivityDto[] = res.fieldValues["Activities"] || [];
        this.activities = items.map((dto) => this.toRow(dto));
      });
  }

  private subscribeToChannel(scenarioId: string): void {
    this.channelName = `scenario-activity-${scenarioId}`;
    this.socketClusterService
      .subscribeToChannel(this.channelName)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((notification: any) => {
        const dto: ScenarioActivityDto = notification.payload;
        if (dto) {
          this.upsertActivity(dto);
        }
      });
  }

  private upsertActivity(dto: ScenarioActivityDto): void {
    const index = this.activities.findIndex(
      (a) => a.eventSourceId === dto.eventSourceId
    );
    const row = this.toRow(dto);

    if (index >= 0) {
      this.activities[index] = {
        ...row,
        completing: this.activities[index].completing,
        removing: this.activities[index].removing,
      };
    } else {
      this.activities.push(row);
    }

    if (dto.status === "Completed" || dto.status === "Closed") {
      this.scheduleRemoval(dto.eventSourceId);
    }
  }

  private scheduleRemoval(eventSourceId: string): void {
    const idx = this.activities.findIndex(
      (a) => a.eventSourceId === eventSourceId
    );
    if (idx < 0) {
      return;
    }
    this.activities[idx].completing = true;

    setTimeout(() => {
      const removeIdx = this.activities.findIndex(
        (a) => a.eventSourceId === eventSourceId
      );
      if (removeIdx >= 0) {
        this.activities[removeIdx].removing = true;

        setTimeout(() => {
          this.activities = this.activities.filter(
            (a) => a.eventSourceId !== eventSourceId
          );
        }, 300);
      }
    }, 3000);
  }

  private toRow(dto: ScenarioActivityDto): ScenarioActivityRow {
    const progressPercent =
      dto.totalSteps > 0
        ? ((dto.totalSteps - dto.remaining) / dto.totalSteps) * 100
        : 0;

    return {
      ...dto,
      completing: false,
      removing: false,
      durationDisplay: this.computeDuration(dto.startedAt),
      progressPercent,
      progressColor: this.getProgressColor(progressPercent),
    };
  }

  private getProgressColor(percent: number): string {
    if (percent < 33) {
      return "#fb5b59";
    }
    if (percent <= 75) {
      return "#FDAC34";
    }
    return "#4BC6A1";
  }

  private startDurationTimer(): void {
    this.durationInterval = setInterval(() => {
      for (const row of this.activities) {
        row.durationDisplay = this.computeDuration(row.startedAt);
      }
    }, 1000);
  }

  private computeDuration(startedAt: string): string {
    if (!startedAt) {
      return "0m 0s";
    }
    const start = new Date(startedAt).getTime();
    const now = Date.now();
    const totalSeconds = Math.max(0, Math.floor((now - start) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }

  getInitials(name: string): string {
    if (!name) {
      return "?";
    }
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }

  getAvatarColor(name: string): string {
    if (!name) {
      return "#9296b5";
    }
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 55%, 50%)`;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case "Starting":
        return "status-starting";
      case "Executing":
        return "status-executing";
      case "Completed":
        return "status-completed";
      default:
        return "status-default";
    }
  }

  trackByEventSourceId(index: number, row: ScenarioActivityRow): string {
    return row.eventSourceId;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    if (this.channelName) {
      this.socketClusterService.unsubscribeChannel(this.channelName);
    }

    if (this.durationInterval) {
      clearInterval(this.durationInterval);
    }
  }
}
